<div class="modal-overlay" *ngIf="result" (click)="close.emit()">
  <div class="sig-panel" (click)="$event.stopPropagation()">

    <div class="sig-panel-header">
      <h3>Signature Properties{{ title ? ' — ' + title : '' }}</h3>
      <button class="modal-close" (click)="close.emit()" aria-label="Close">✕</button>
    </div>

    <p class="sig-empty" *ngIf="result.signatures.length === 0">
      This document carries no digital signature.
    </p>

    <ng-container *ngIf="signature as s">

      <!-- Signature selector, only when the document carries more than one -->
      <div class="sig-selector" *ngIf="result.signatures.length > 1">
        <button
          type="button"
          *ngFor="let e of result.signatures; let i = index"
          class="sig-selector-btn"
          [class.active]="i === activeIndex"
          (click)="selectSignature(i)"
        >
          <span class="sig-dot" [ngClass]="'dot-' + e.status.toLowerCase()"></span>
          {{ e.field || 'Signature ' + (i + 1) }}
        </button>
      </div>

      <div class="sig-tabs">
        <button type="button" [class.active]="tab === 'summary'" (click)="tab = 'summary'">Summary</button>
        <button type="button" [class.active]="tab === 'certificate'" (click)="tab = 'certificate'">Certificate</button>
        <button type="button" [class.active]="tab === 'trust'" (click)="tab = 'trust'">Trust</button>
        <button type="button" [class.active]="tab === 'revocation'" (click)="tab = 'revocation'">Revocation</button>
      </div>

      <div class="sig-body">

        <!-- ============================ SUMMARY ============================ -->
        <ng-container *ngIf="tab === 'summary'">
          <div class="sig-headline" [ngClass]="'headline-' + s.status.toLowerCase()">
            <span class="sig-dot" [ngClass]="'dot-' + s.status.toLowerCase()"></span>
            <span>{{ headline }}</span>
          </div>

          <div class="sig-signer" *ngIf="s.signerCommonName">
            {{ s.signerCommonName }}<span *ngIf="s.signerOrganization"> — {{ s.signerOrganization }}</span>
          </div>

          <ul class="sig-statements">
            <li *ngIf="fromTrustedLists && trustedListsCheckedAt">
              Source of Trust obtained from the EU Trusted Lists, last checked on
              {{ formatDate(trustedListsCheckedAt) }}.
            </li>
            <li [class.bad]="!s.intact">{{ integrityStatement }}</li>
            <li [class.bad]="!s.trusted">{{ identityStatement }}</li>
            <li [class.warn]="s.adesLevel !== 'B-T'">{{ signingTimeStatement }}</li>
            <li class="warn">{{ ltvStatement }}</li>
            <li *ngIf="qualificationStatement" [class.good]="s.qualification?.qualified">
              {{ qualificationStatement }}
            </li>
            <li class="muted" *ngIf="!s.qualification">
              Qualified status under Regulation (EU) 910/2014 was not determined —
              this is not a finding that the certificate is unqualified.
            </li>
            <li class="bad" *ngIf="s.docMdpOk === false">
              A change made after signing violates the permissions set by the certifying signature.
            </li>
            <li class="bad" *ngIf="s.error">Verification error: {{ s.error }}</li>
          </ul>

          <h4 class="sig-section-title">Signature Details</h4>
          <div class="sig-row">
            <span class="sig-label">Field</span>
            <span class="sig-value">{{ s.field || '—' }}</span>
          </div>
          <div class="sig-row">
            <span class="sig-label">Signing time</span>
            <span class="sig-value">{{ formatDate(s.signingTime) }}</span>
          </div>
          <div class="sig-row" *ngIf="s.timestampTime">
            <span class="sig-label">Timestamp</span>
            <span class="sig-value">{{ formatDate(s.timestampTime) }}</span>
          </div>
          <div class="sig-row">
            <span class="sig-label">Coverage</span>
            <span class="sig-value">{{ coverageLabel(s.coverage) }}</span>
          </div>
          <div class="sig-row">
            <span class="sig-label">Last checked</span>
            <span class="sig-value">
              {{ formatDate(result.verifiedAt) }}
              <span class="sig-hint" *ngIf="result.verifiedAt">
                (verified when the file was uploaded; not re-checked on opening this panel)
              </span>
            </span>
          </div>
          <div class="sig-actions">
            <button type="button" class="btn-secondary" (click)="tab = 'certificate'">
              Certificate Details…
            </button>
            <button
              type="button"
              class="btn-secondary"
              *ngIf="dossierId && uploadId"
              [disabled]="!canRecheck"
              (click)="recheck()"
            >
              {{ rechecking ? 'Verifying…' : 'Validate Signature' }}
            </button>
            <span class="sig-hint bad-text" *ngIf="recheckError">{{ recheckError }}</span>
          </div>
        </ng-container>

        <!-- ========================== CERTIFICATE ========================== -->
        <ng-container *ngIf="tab === 'certificate'">
          <div class="cert-layout">
            <div class="cert-tree">
              <div class="cert-tree-title">Certificate chain</div>
              <button
                type="button"
                *ngFor="let c of chain; let i = index"
                class="cert-tree-node"
                [class.active]="i === selectedCertIndex"
                [style.paddingLeft.rem]="0.5 + i * 0.9"
                (click)="selectedCertIndex = i"
              >
                <span class="cert-tree-branch" *ngIf="i > 0">└</span>
                {{ c.subject.commonName || c.subject.organization || '(unnamed)' }}
              </button>
              <p class="sig-hint" *ngIf="!signature?.chain?.length">
                No validated path was built, so only the signer’s own certificate is shown.
              </p>
            </div>

            <div class="cert-detail" *ngIf="selectedCert as c">
              <div class="sig-row">
                <span class="sig-label">Common name (CN)</span>
                <span class="sig-value">{{ c.subject.commonName || '—' }}</span>
              </div>
              <div class="sig-row" *ngIf="c.subject.organization">
                <span class="sig-label">Organization (O)</span>
                <span class="sig-value">{{ c.subject.organization }}</span>
              </div>
              <div class="sig-row" *ngIf="c.subject.organizationalUnit">
                <span class="sig-label">Organizational unit (OU)</span>
                <span class="sig-value">{{ c.subject.organizationalUnit }}</span>
              </div>
              <div class="sig-row" *ngIf="c.subject.country">
                <span class="sig-label">Country (C)</span>
                <span class="sig-value">{{ c.subject.country }}</span>
              </div>
              <div class="sig-row" *ngIf="c.subject.serialNumber">
                <span class="sig-label">Subject serial number</span>
                <span class="sig-value">{{ c.subject.serialNumber }}</span>
              </div>

              <div class="sig-row">
                <span class="sig-label">Issued by</span>
                <span class="sig-value">
                  {{ c.issuer.commonName || '—' }}<span *ngIf="c.issuer.organization"> ({{ c.issuer.organization }})</span>
                </span>
              </div>
              <div class="sig-row">
                <span class="sig-label">Certificate serial</span>
                <span class="sig-value mono">{{ c.serial || '—' }}</span>
              </div>
              <div class="sig-row">
                <span class="sig-label">Valid from</span>
                <span class="sig-value">{{ formatDate(c.validFrom) }}</span>
              </div>
              <div class="sig-row">
                <span class="sig-label">Valid to</span>
                <span class="sig-value">{{ formatDate(c.validTo) }}</span>
              </div>
              <div class="sig-row">
                <span class="sig-label">Intended usage</span>
                <span class="sig-value">
                  <span *ngIf="intendedUsage.length === 0">Not restricted by the certificate</span>
                  <span *ngFor="let u of intendedUsage; let last = last">{{ u }}<span *ngIf="!last">, </span></span>
                </span>
              </div>
              <div class="sig-row" *ngIf="c.sha256">
                <span class="sig-label">SHA-256 fingerprint</span>
                <span class="sig-value mono small">{{ c.sha256 }}</span>
              </div>

              <p class="cert-annex" *ngIf="annexNote">{{ annexNote }}</p>

              <div class="sig-actions">
                <button
                  type="button"
                  class="btn-secondary"
                  [disabled]="!c.derB64"
                  (click)="exportCertificate()"
                >
                  Export…
                </button>
                <span class="sig-hint" *ngIf="!c.derB64">
                  This certificate’s bytes were not recorded, so it cannot be exported.
                </span>
              </div>
            </div>
          </div>
        </ng-container>

        <!-- ============================= TRUST ============================= -->
        <ng-container *ngIf="tab === 'trust'">
          <p class="trust-source">{{ trustSourceStatement }}</p>

          <div class="trust-box">
            <div class="trust-box-title">This certificate is trusted to</div>
            <div class="trust-item" *ngFor="let t of trustSettings">
              <span class="trust-mark" [class.granted]="t.granted">{{ t.granted ? '✓' : '✕' }}</span>
              {{ t.label }}
            </div>
          </div>

          <div class="trust-stats" *ngIf="result.trustedLists?.stats as st">
            EU Trusted Lists at verification time: {{ st.certificate_authorities }} certificate
            authorities and {{ st.timestamp_authorities }} timestamp authorities
            <span *ngIf="st.download_failures.length">
              ({{ st.download_failures.length }} national list(s) could not be downloaded)</span>.
          </div>

          <div class="sig-actions">
            <button type="button" class="btn-secondary" disabled>Add to Trusted Certificates</button>
            <span class="sig-hint">
              A per-organization trust store is not available yet. Trust currently comes from the
              EU Trusted Lists and from anchors configured for this deployment; adding your own
              anchors would need to be scoped to your organization so it cannot affect other tenants.
            </span>
          </div>
        </ng-container>

        <!-- ========================== REVOCATION =========================== -->
        <ng-container *ngIf="tab === 'revocation'">
          <p class="sig-hint">
            Revocation was not checked against a live OCSP responder or CRL distribution point.
            Only revocation material already embedded in the document is considered, so this
            panel cannot state that the certificate is still valid <em>today</em>.
          </p>

          <div class="sig-row">
            <span class="sig-label">Real-time check</span>
            <span class="sig-value">{{ s.revocation?.checkedOnline ? 'Performed' : 'Not performed' }}</span>
          </div>
          <div class="sig-row">
            <span class="sig-label">Signer</span>
            <span class="sig-value">{{ s.signerCommonName || '—' }}</span>
          </div>
          <div class="sig-row">
            <span class="sig-label">Result</span>
            <span class="sig-value" [class.bad-text]="s.revocation?.revoked">
              {{ s.revocation?.revoked ? 'Certificate reported REVOKED' : 'No revocation reported' }}
            </span>
          </div>
          <ng-container *ngIf="s.revocation?.details as d">
            <div class="sig-row" *ngIf="d.revocationDate">
              <span class="sig-label">Revoked on</span>
              <span class="sig-value">{{ formatDate(d.revocationDate) }}</span>
            </div>
            <div class="sig-row" *ngIf="d.revocationReason">
              <span class="sig-label">Reason</span>
              <span class="sig-value">{{ d.revocationReason }}</span>
            </div>
            <div class="sig-row" *ngIf="d.caRevoked">
              <span class="sig-label">Problems encountered</span>
              <span class="sig-value bad-text">An issuing CA in the chain is itself revoked.</span>
            </div>
          </ng-container>
        </ng-container>

      </div>

      <div class="sig-legal">
        <strong>Legal notice.</strong> This panel reports the result of a technical
        verification of the cryptographic signatures embedded in the file, performed against the
        EU Trusted Lists as published by the European Commission. It is not legal advice and is
        not an attestation by Irisel as to the authenticity, completeness or legal effect of the
        underlying document. Whether a signature has legal effect in a given jurisdiction is a
        question for the parties and their counsel.
      </div>

    </ng-container>
  </div>
</div>
